'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDeleteUpsells } from '@/lib/api/upsells';
import { useAuth } from '@clerk/clerk-react';

interface BulkDeleteUpsellsModalProps {
    upsellIds: string[];
    storeId: string;
    onClose: () => void;
    onSuccess?: () => void;
}

/**
 * BulkDeleteUpsellsModal component for confirming bulk deletion of upsells
 */
export const BulkDeleteUpsellsModal = ({
    upsellIds,
    storeId,
    onClose,
    onSuccess,
}: BulkDeleteUpsellsModalProps) => {
    const { t } = useTranslation('upsells');
    const { orgId } = useAuth();
    const deleteUpsellsMutation = useDeleteUpsells();

    const handleDelete = async () => {
        if (!orgId) return;
        await deleteUpsellsMutation.mutateAsync({ ids: upsellIds });
        onSuccess?.();
        onClose();
    };

    return (
        <AlertDialog open={!!upsellIds.length} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('modals.bulkDelete.title', { count: upsellIds.length })}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t('modals.bulkDelete.description')}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={onClose}>{t('actions.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={handleDelete}
                        disabled={deleteUpsellsMutation.isPending}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        {deleteUpsellsMutation.isPending ? t('modals.delete.deleting') : t('modals.delete.delete')}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
